import React from 'react';
import { Box } from 'src/design/atoms/Box';
import { Collapse } from 'src/design/atoms/Collapse';
import { NavLink } from 'src/design/atoms/NavLink';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useAuth } from 'src/hooks/useAuth';
import { useMenu } from 'src/hooks/useMenu';
import { INavigationItem } from 'src/app/navigation/navigationTypes';
import { NavItem } from 'src/app/navigation/NavItem';

export interface INavCollapseProps {
  item: INavigationItem;
  level: number;
}

const NavCollapse: React.FC<INavCollapseProps> = ({ item, level }) => {
  const { drawerOpen } = useMenu();
  const { checkFeatureAllowed } = useAuth();
  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  const isAllowed = item.allowAll
    ? checkFeatureAllowed(item.requiredScopes)
    : checkFeatureAllowed(item.requiredScopes, item.id);

  if (!isAllowed && !item.allowAll) return null;

  const collapseContent = item.children?.map((menuItem) => {
    switch (menuItem.type) {
      case 'item':
        return <NavItem key={menuItem.id} item={menuItem} level={level + 1} />;
      case 'collapse':
        return (
          <NavCollapse key={menuItem.id} item={menuItem} level={level + 1} />
        );
      default:
        return null;
    }
  });

  return (
    <Box>
      <NavLink
        label={
          drawerOpen || (!drawerOpen && level !== 1) ? item.title : undefined
        }
        leftSection={item.icon}
        rightSection={
          drawerOpen || (!drawerOpen && level !== 1) ? (
            open ? (
              <IconChevronUp size={18} />
            ) : (
              <IconChevronDown size={18} />
            )
          ) : undefined
        }
        disabled={item.disabled}
        onClick={handleClick}
        pl={drawerOpen ? level * 28 : undefined}
      />
      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <Collapse in={open}>
          <Stack gap={0}>{collapseContent}</Stack>
        </Collapse>
      )}
    </Box>
  );
};

export interface INavGroupProps {
  item: INavigationItem;
}

/** @registry Sidebar nav group heading + its collapse/item children; hides the whole group when no child passes scope checks. */
export const NavGroup: React.FC<INavGroupProps> = ({ item }) => {
  const { drawerOpen } = useMenu();
  const { checkFeatureAllowed } = useAuth();

  const hasPermittedChildren = item.children?.some((child) => {
    const isAllowed = child.allowAll
      ? checkFeatureAllowed(child.requiredScopes)
      : checkFeatureAllowed(child.requiredScopes, child.id);
    return isAllowed || child.allowAll;
  });

  if (!hasPermittedChildren) return null;

  const navCollapse = item.children?.map((menuItem) => {
    switch (menuItem.type) {
      case 'collapse':
        return <NavCollapse key={menuItem.id} item={menuItem} level={1} />;
      case 'item':
        return <NavItem key={menuItem.id} item={menuItem} level={1} />;
      default:
        return (
          <Text key={menuItem.id} c="red" ta="center" fw={600}>
            Fix - Group Collapse or Items
          </Text>
        );
    }
  });

  return (
    <Box mb={drawerOpen ? 'md' : 0} py={0}>
      {item.title && drawerOpen && (
        <Box pl="lg" mb="xs">
          <Text size="xs" fw={600} tt="uppercase" c="dimmed">
            {item.title}
          </Text>
        </Box>
      )}
      <Stack gap={0}>{navCollapse}</Stack>
    </Box>
  );
};
